/**
 * Shop time.
 *
 * The shop is in Bangkok, so the shop's clock is the only clock that matters.
 * A receptionist on holiday in London, a guest booking from Sydney and the
 * owner checking takings from Vancouver must all see the same 14:30 — the one
 * the therapist and the bed are actually booked for.
 *
 * So nothing in the app may use the browser's local zone. `new Date()`,
 * `toLocaleString()` and `toTimeString()` all silently read the viewer's
 * machine; every one of them is replaced by a helper here that names the zone.
 *
 * Thailand has had no daylight saving since 1920 and sits at a flat UTC+7, but
 * the offset is still derived rather than hardcoded so a second branch in
 * another country would not quietly produce wrong instants.
 */

export const SHOP_TZ = process.env.REACT_APP_SHOP_TZ || 'Asia/Bangkok';

/**
 * Thai UI, Gregorian years. Plain 'th-TH' renders 2026 as the Buddhist-era
 * 2569, which is not what the shop's POS shows — the -u-ca-gregory extension
 * keeps Thai month and weekday names but the Gregorian year.
 */
export const TH_LOCALE = 'th-TH-u-ca-gregory';

const partsIn = (date, opts) => {
  const out = {};
  for (const { type, value } of new Intl.DateTimeFormat('en-US', {
    timeZone: SHOP_TZ, hour12: false, ...opts,
  }).formatToParts(date)) {
    out[type] = value;
  }
  return out;
};

/** How far `SHOP_TZ` runs ahead of UTC at this instant, in minutes. */
const offsetMinutesAt = (date) => {
  const p = partsIn(date, {
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', second: '2-digit',
  });
  const wallAsUtc = Date.UTC(
    Number(p.year), Number(p.month) - 1, Number(p.day),
    Number(p.hour) % 24, Number(p.minute), Number(p.second),
  );
  // Truncate to whole seconds on both sides — `wallAsUtc` has no milliseconds,
  // and leaving them in would push the offset off by a fraction of a minute.
  return (wallAsUtc - Math.floor(date.getTime() / 1000) * 1000) / 60000;
};

/* ------------------------------------------------------------------ "now" */

/** Today's calendar day at the shop — not the viewer's today. */
export const todayIso = (at = new Date()) => {
  const p = partsIn(at, { year: 'numeric', month: '2-digit', day: '2-digit' });
  return `${p.year}-${p.month}-${p.day}`;
};

/** The wall clock at the shop right now, as HH:MM. */
export const nowHhmm = (at = new Date()) => {
  const p = partsIn(at, { hour: '2-digit', minute: '2-digit' });
  return `${String(Number(p.hour) % 24).padStart(2, '0')}:${p.minute}`;
};

/* ------------------------------------------------- wall time -> instant */

/**
 * `('2026-07-28', '14:30')` -> the Date that IS 14:30 in Bangkok.
 *
 * The wall time is first read as if it were UTC, then shifted by the zone's
 * offset. The offset is resolved twice because the first guess can land on the
 * wrong side of a DST change — a no-op in Thailand, correct everywhere else.
 */
export const shopInstant = (dayIso, hhmm = '00:00') => {
  const [y, m, d] = String(dayIso).split('-').map(Number);
  const [hh, mm] = String(hhmm).split(':').map(Number);
  const naive = Date.UTC(y, m - 1, d, hh || 0, mm || 0);

  let instant = new Date(naive - offsetMinutesAt(new Date(naive)) * 60000);
  instant = new Date(naive - offsetMinutesAt(instant) * 60000);
  return instant;
};

/** The same, ready for the API. */
export const shopInstantIso = (dayIso, hhmm) => shopInstant(dayIso, hhmm).toISOString();

/* ------------------------------------------------------------ formatting */

/** 14:30 — the shop's wall clock, whoever is looking. */
export const timeOf = (iso) =>
  iso
    ? new Date(iso).toLocaleTimeString(TH_LOCALE, {
        timeZone: SHOP_TZ, hour: '2-digit', minute: '2-digit', hour12: false,
      })
    : '';

/** 28/07/2026 in the shop's day, whoever is looking. */
export const dateOf = (iso) =>
  iso
    ? new Date(iso).toLocaleDateString('en-GB', {
        timeZone: SHOP_TZ, day: '2-digit', month: '2-digit', year: 'numeric',
      })
    : '';

/** 28/07/2026 14:30 */
export const dateTimeOf = (iso) => (iso ? `${dateOf(iso)} ${timeOf(iso)}` : '');

/** 28 ก.ค. 2026 — the Thai short form used in tables. */
export const dateThShort = (iso) =>
  iso
    ? new Date(iso).toLocaleDateString(TH_LOCALE, {
        timeZone: SHOP_TZ, day: 'numeric', month: 'short', year: 'numeric',
      })
    : '';

/** The calendar day an instant falls on at the shop. */
export const dayIsoOf = (iso) => (iso ? todayIso(new Date(iso)) : '');

/**
 * 'จันทร์, 28 กรกฎาคม 2026' — the toolbar format, from a calendar-day string.
 *
 * A bare 'YYYY-MM-DD' is a day, not an instant, so it is anchored at midday UTC
 * and read back in UTC. Anchoring at midnight would let a viewer behind UTC
 * render the previous day.
 */
export const dateTh = (dayIso) => {
  if (!dayIso) return '';
  const dt = new Date(`${dayIso}T12:00:00Z`);
  const opts = { timeZone: 'UTC' };
  const weekday = dt.toLocaleDateString(TH_LOCALE, { ...opts, weekday: 'long' }).replace(/^วัน/, '');
  const rest = dt.toLocaleDateString(TH_LOCALE, {
    ...opts, day: 'numeric', month: 'long', year: 'numeric',
  });
  return `${weekday}, ${rest}`;
};

/** A calendar day formatted in any locale, without drifting a day. */
export const dayLabel = (dayIso, locale, opts) =>
  new Date(`${dayIso}T12:00:00Z`).toLocaleDateString(locale, { timeZone: 'UTC', ...opts });

/** Calendar-day arithmetic, free of any zone. */
export const shiftDate = (dayIso, days) => {
  const d = new Date(`${dayIso}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
};

/** Minutes since midnight at the shop — how the board positions a card. */
export const minutesOfDay = (iso, tz = SHOP_TZ) => {
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone: tz, hour: '2-digit', minute: '2-digit', hour12: false,
  }).formatToParts(new Date(iso));
  const h = Number(parts.find((p) => p.type === 'hour').value) % 24;
  const m = Number(parts.find((p) => p.type === 'minute').value);
  return h * 60 + m;
};
